import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Position } from '../../shared/modal/PositionModal';
import { Text } from '../../shared/text/Text';
import styles from './SharedStyling.module.scss';
import { SearchFieldSection } from '../../datasets/instances/store/types';
import { OpenFieldType } from '../../datasets/store/types';
import { FuzzyFieldMatchModal } from './FuzzyFieldMatchModal';

interface FieldSearchProps {
    searchFieldSection: SearchFieldSection | null;
    fuzzyMatches: SearchFieldSection[];
    searchValue: string;
    updateSearchFieldSection: (match: SearchFieldSection | null) => void;
    updateSearchFuzzyMatches: (fuzzyMatches: SearchFieldSection[]) => void;
    updateFuzzyMatchSearchValue: (value: string) => void;
    id: string;
}

export const FieldSearch: React.FC<FieldSearchProps> = ({ searchFieldSection, fuzzyMatches, searchValue, updateSearchFieldSection, updateFuzzyMatchSearchValue, updateSearchFuzzyMatches, id }) => {
    const [shouldOpen, setShouldOpen] = useState<boolean>(true);
    const [fuzzyMatchIndex, setFuzzyMatchIndex] = useState<number>(0);
    const searchInputRef = useRef<HTMLDivElement>(null);

    const xPosition = searchInputRef.current?.offsetLeft ? searchInputRef.current.offsetLeft + 1 : 0;
    const yPosition = searchInputRef.current?.offsetTop ? searchInputRef.current.offsetTop + 145 : 0;
    const inputWidth = searchInputRef.current?.offsetWidth ? searchInputRef.current.offsetWidth - 2 : 0;
    const fieldMatchPosition: Position = { x: xPosition, y: yPosition };

    const isOpen = useMemo(() => fuzzyMatches.length > 0 && shouldOpen, [shouldOpen, fuzzyMatches]);

    const scrollToSearchMatch = useCallback((index: number) => {
        const searchMatch = document.getElementById(`${id}-field-match-${index}`);
        searchMatch?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [id]);

    const scrollToFieldSection = useCallback((match: SearchFieldSection) => {
        let ref: HTMLElement | null = null;
        if (match.type === OpenFieldType.SECTION) {
            ref = document.getElementById(`${id}-section-${match.sectionId}`);

        } else {
            ref = document.getElementById(`${id}-field-${match.fieldId}`);
        }
        ref?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [id]);

    const resetSearch = useCallback(() => {
        updateSearchFieldSection(null);
        updateSearchFuzzyMatches([]);
    }, [updateSearchFieldSection, updateSearchFuzzyMatches]);

    const selectMatch = useCallback((match: SearchFieldSection) => {
        updateSearchFieldSection(match);
        setShouldOpen(false);
        setTimeout(() => scrollToFieldSection(match), 150);
        setTimeout(() => resetSearch(), 3000);
    }, [updateSearchFieldSection, scrollToFieldSection, resetSearch]);

    const onKeyDown = useCallback((e: KeyboardEvent) => {
        if (isOpen) {
            const maxIndex = fuzzyMatches.length - 1;
            if (e.key.toLowerCase() === 'enter') {
                const match = fuzzyMatches[fuzzyMatchIndex];
                selectMatch(match);
            }
            if (e.key.toLowerCase() === 'arrowup') {
                const indexToUpdate = fuzzyMatchIndex === 0 ? maxIndex : fuzzyMatchIndex - 1;
                setFuzzyMatchIndex(indexToUpdate);
                scrollToSearchMatch(indexToUpdate);
            }
            if (e.key.toLowerCase() === 'arrowdown') {
                const indexToUpdate = fuzzyMatchIndex === maxIndex ? 0 : fuzzyMatchIndex + 1;
                setFuzzyMatchIndex(indexToUpdate);
                scrollToSearchMatch(indexToUpdate);
            }
        }
    }, [setFuzzyMatchIndex, fuzzyMatches, fuzzyMatchIndex, isOpen, scrollToSearchMatch, selectMatch]);

    useEffect(() => {
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [onKeyDown]);

    const updateSearchTerm = useCallback((value: string) => {
        setShouldOpen(true);
        updateFuzzyMatchSearchValue(value);
        setFuzzyMatchIndex(0);
    }, [updateFuzzyMatchSearchValue]);

    const closeModal = useCallback(() => {
        setShouldOpen(false);
        setTimeout(() => resetSearch(), 3000);
    }, [resetSearch]);

    useEffect(() => () => {
        resetSearch();
        updateFuzzyMatchSearchValue('');
    }, [resetSearch, updateFuzzyMatchSearchValue]);

    useEffect(() => {
        !isNull(searchFieldSection) ? scrollToFieldSection(searchFieldSection) : null;
    }, [searchFieldSection, scrollToFieldSection]);

    return (
        <div className={styles.fieldSearchWrapper} data-testid='${id}-field-search' ref={searchInputRef}>
            <Text
                testId={`${id}-search`}
                placeholder='Find a data point...'
                value={searchValue}
                onChange={e => updateSearchTerm(e.target.value)}
                marginBottom='0'
            />
            <FuzzyFieldMatchModal
                isOpen={isOpen}
                position={fieldMatchPosition}
                inputWidth={inputWidth}
                selectMatch={selectMatch}
                closeModal={closeModal}
                fuzzyMatches={fuzzyMatches}
                searchValue={searchValue}
                fuzzyMatchIndex={fuzzyMatchIndex}
                setFuzzyMatchIndex={index => setFuzzyMatchIndex(index)}
                id={id}
            />
        </div>
    );
};
